/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import {
  saveCalendarExercise, updateProgrammedCalendarItem, swapProgrammedItemOnChildCalendar, setCombineWithInfo,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const alreadySavedGlobal = new Set();

const HealthTable = ({
  contentIndex,
  content,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const formatDate = () => `${moment(date).format('YYYY-MM-DD')}`;
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);

  const handleAutoSaveCalendarNote = (contentIndexParam) => {
    const submissionObject = {
      date: moment(date).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      type: 'health',
      showSessionBreakItems: false,
      order: contentIndex,
    };
    if (content.description) {
      submissionObject.description = content.description;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
    }
    const formattedDate = moment(date).format('YYYY-MM-DD');
    dispatch(saveCalendarExercise(
      currentUser,
      contentIndexParam,
      submissionObject,
      formattedDate,
      () => {},
      {},
      content.assignedId,
    ));
  };

  /** the CellMeasurer wrapped around each row in the virtualized list
    * can cause remounting of the item a few times as it measures
    * the "just in time" height, this can cause multiple firings of this
    * handleAutoSave funtion (which is a problem because it will create 3 of the same items in the data base).
    * To get around that we save the item by id
    * in the global set that will persist renders and if we have alreay saved
    * the item them we will not fire that function again.
    */

  useEffect(() => {
    if (content.preSave && !alreadySavedGlobal.has(content.assignedId)) {
      alreadySavedGlobal.add(content.assignedId);
      handleAutoSaveCalendarNote(contentIndex);
    }
  }, [content.assignedId]);

  /** This useEffect listens for changes is the exercise chosen from the
    * table dropdown menu
    */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        assignedId: content.assignedId,
        date: formatDate(date),
        exerciseId: swapDropdownSelectedExercise.exerciseId,
        type: 'health',
        order: contentIndex,
      };
      if (nonSwappedItemFromParentCalendar) {
        const reorder = false;
        dispatch(swapProgrammedItemOnChildCalendar(
          currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
            onSuccess: () => {},
            onError: () => {},
          },
        ));
      } else {
        const reorder = false;
        dispatch(updateProgrammedCalendarItem(
          currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
            onSuccess: () => {},
            onError: () => {},
          },
        ));
      }
    }
  }, [swapDropdownSelectedExercise, nonSwappedItemFromParentCalendar]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (!content.preSave && combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));
      const updatedItem = {
        ...content,
        assignedId: content.assignedId,
        date: formatDate(date),
        exerciseId: content.exerciseId,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,

        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
        type: 'health',
      };
      if (nonSwappedItemFromParentCalendar) {
        const reorder = false;
        dispatch(swapProgrammedItemOnChildCalendar(
          currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
            onSuccess: () => {},
            onError: () => {},
          },
        ));
      } else {
        const reorder = true;
        dispatch(updateProgrammedCalendarItem(
          currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
            onSuccess: () => {},
            onError: () => {},
          },
        ));
      }
    }
  }, [combineWithInfo, nonSwappedItemFromParentCalendar]);

  return (
    <Container />
  );
};

HealthTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

HealthTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default HealthTable;
